/* import __COLOCATED_TEMPLATE__ from './preview-email.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { task } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import { type TrackedArray } from 'tracked-built-ins';
import type Conversation from 'embercom/objects/inbox/conversation';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';

const MESSAGE_TYPES = new Set([
  RenderableType.UserComment,
  RenderableType.UserEmailComment,
  RenderableType.BotComment,
  RenderableType.BotNote,
  RenderableType.AdminComment,
  RenderableType.AdminNote,
  RenderableType.FinAnswer,
  RenderableType.Email,
  RenderableType.Chat,
  RenderableType.CustomBot,
]);

interface Signature {
  Args: {
    selectedAudiences?: TrackedArray<string>;
    conversation?: Conversation;
    previewSessionId?: string;
    onSendEmail?: (body: string, subject: string) => Promise<void>;
    onSendReply?: (body: string) => Promise<void>;
  };
  Element: HTMLDivElement;
}

export default class PreviewEmail extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare inboxApi: any;

  @tracked declare finPreviewAddress: string;
  @tracked emailSubject = '';
  @tracked emailBody = '';
  @tracked isSending = false;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    if (!this.canUseInlinePreview) {
      taskFor(this.fetchFinPreviewAddress).perform();
    }
  }

  get canUseInlinePreview(): boolean {
    return this.appService.app.canUseEmailPreviewInbox;
  }

  get showComposeForm(): boolean {
    return this.canUseInlinePreview && !this.args.conversation;
  }

  get showConversation(): boolean {
    return this.canUseInlinePreview && !!this.args.conversation;
  }

  get canSend(): boolean {
    return this.emailBody.trim().length > 0 && !this.isSending;
  }

  @action
  updateSubject(event: Event) {
    this.emailSubject = (event.target as HTMLInputElement).value;
  }

  @action
  updateBody(event: Event) {
    this.emailBody = (event.target as HTMLTextAreaElement).value;
  }

  @action
  async sendEmail() {
    if (!this.canSend || !this.args.onSendEmail) {
      return;
    }

    this.isSending = true;

    try {
      await this.args.onSendEmail(this.emailBody, this.emailSubject);
      this.resetComposeForm();

      this.intercomEventService.trackAnalyticsEvent({
        action: 'sent',
        object: 'preview_email',
        place: 'workflow_preview',
        section: 'preview',
      });
    } finally {
      this.isSending = false;
    }
  }

  @action
  async sendReply() {
    if (!this.canSend || !this.args.onSendReply) {
      return;
    }

    this.isSending = true;

    try {
      await this.args.onSendReply(this.emailBody);
      this.emailBody = '';
    } finally {
      this.isSending = false;
    }
  }

  @action
  resetComposeForm() {
    this.emailSubject = '';
    this.emailBody = '';
  }

  // Legacy copy-address flow (when feature flag is off)
  @task({ drop: true })
  *fetchFinPreviewAddress(): TaskGenerator<void> {
    let finPreviewSettings = yield this.store.findRecord(
      'ai-agent/preview-settings',
      this.appService.app.id,
    );

    this.finPreviewAddress = finPreviewSettings.previewAddress;
  }

  get isFetchingFinPreviewAddress() {
    return taskFor(this.fetchFinPreviewAddress).isRunning;
  }

  get displayedPreviewAddress(): string {
    if (!this.finPreviewAddress) {
      return '';
    }

    if (this.args.selectedAudiences?.length === 1) {
      let audienceId = this.args.selectedAudiences[0];
      let [localPart, domain] = this.finPreviewAddress.split('@');
      return `${localPart}+${audienceId}@${domain}`;
    }

    return this.finPreviewAddress;
  }

  get messageParts(): Array<{ part: RenderablePart; isUser: boolean; text: string }> {
    if (!this.args.conversation) {
      return [];
    }

    return this.args.conversation.renderableParts
      .filter((part: RenderablePart) => MESSAGE_TYPES.has(part.renderableType))
      .map((part: RenderablePart) => ({
        part,
        isUser: part.createdByUser,
        text: this.extractText(part),
      }))
      .filter((item) => item.text.length > 0);
  }

  extractText(part: RenderablePart): string {
    let data = part.renderableData as any;
    if (data.blocks?.length) {
      return data.blocks
        .map((b: any) => {
          if (b.text) {
            return b.text;
          }
          if (b.html) {
            return b.html.replace(/<[^>]*>/g, '').trim();
          }
          if (b.content) {
            return b.content.replace(/<[^>]*>/g, '').trim();
          }
          if (b.items?.length) {
            return b.items.join(' ');
          }
          return '';
        })
        .filter(Boolean)
        .join('\n');
    }
    if (data.text) {
      return data.text;
    }
    return '';
  }

  @action
  trackCopyEmailAddress() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'preview_email_copy_address',
      place: 'workflow_preview',
      section: 'preview',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Preview::PreviewEmail': typeof PreviewEmail;
    'workflows/preview/preview-email': typeof PreviewEmail;
  }
}
